import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getAttachmentUrl } from '../utils/attachmentUtils';
import { 
  FileText, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Save, 
  X, 
  Bold, 
  Italic, 
  Underline, 
  Link, 
  List, 
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Type,
  Palette,
  History,
  Tag,
  Globe,
  Lock,
  ArrowLeft,
  Upload,
  File,
  ExternalLink,
  Code,
  Image,
  Paperclip,
  Download,
  Copy,
  CheckCircle,
  AlertCircle,
  Video,
  FileSpreadsheet,
  FileCode,
  FileType,
  Grid,
  BarChart3,
  Filter,
  SortAsc,
  SortDesc,
  Calendar,
  User,
  Clock,
  Star,
  Bookmark,
  MoreVertical,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Helper function to get authenticated file URL
const getAuthenticatedFileUrl = async (fileId) => {
  try {
    const response = await axios.get(`/api/documentation/files/${fileId}`, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error fetching file:', error);
    return null;
  }
};

// Helper function to get fresh URL for a document
const getFreshDocumentUrl = async (fileId) => {
  try {
    const response = await axios.get(`/api/documentation/files/${fileId}/fresh-url`);
    return response.data.fresh_url;
  } catch (error) {
    console.error('Error getting fresh URL:', error);
    return null;
  }
};

// Helper function to open document with fresh URL
const openDocumentWithFreshUrl = async (fileId, filename) => {
  try {
    // First try to get a fresh URL
    const freshUrl = await getFreshDocumentUrl(fileId);
    if (freshUrl) {
      window.open(freshUrl, '_blank');
      return true;
    }
    
    // Fallback to authenticated file URL
    const url = await getAuthenticatedFileUrl(fileId);
    if (url) {
      window.open(url, '_blank');
      return true;
    }
    
    toast.error('Failed to open document. Please try again.');
    return false;
  } catch (error) {
    console.error('Error opening document:', error);
    toast.error('Failed to open document. Please try again.');
    return false;
  }
};

// Helper function to download document with fresh URL
const downloadDocumentWithFreshUrl = async (fileId, filename) => {
  try {
    // First try to get a fresh URL
    const freshUrl = await getFreshDocumentUrl(fileId);
    if (freshUrl) {
      const link = document.createElement('a');
      link.href = freshUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    }
    
    // Fallback to authenticated file URL
    const url = await getAuthenticatedFileUrl(fileId);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    }
    
    toast.error('Failed to download document. Please try again.');
    return false;
  } catch (error) {
    console.error('Error downloading document:', error);
    toast.error('Failed to download document. Please try again.');
    return false;
  }
};

// Office Preview Component with fresh URL handling
const OfficePreview = ({ fileId, filename }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadPreview = async () => {
      setLoading(true);
      setError(false);
      
      try {
        const freshUrl = await getFreshDocumentUrl(fileId);
        if (freshUrl) {
          setPreviewUrl(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(freshUrl)}`);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Error loading preview:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [fileId]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !previewUrl) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
        <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
          <FileType className="h-10 w-10 text-gray-500" />
        </div>
        <p className="text-gray-600 mb-4">Preview not available</p>
        <button
          onClick={() => openDocumentWithFreshUrl(fileId, filename)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Open in New Tab
        </button>
      </div>
    );
  }

  return (
    <iframe
      src={previewUrl}
      className="w-full h-full border-0"
      title={`Preview of ${filename}`}
      onError={(e) => {
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'flex';
      }}
    />
  );
};

// Authenticated File Preview Component
const AuthenticatedFilePreview = ({ fileId, filename, fileType, className, style, ...props }) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadFile = async () => {
      setLoading(true);
      setError(false);
      
      const fileExtension = filename.toLowerCase().split('.').pop();
      
      // For text-based files, fetch content as text
      if (['json', 'yaml', 'yml', 'xml', 'txt', 'md'].includes(fileExtension)) {
        try {
          const response = await axios.get(`/api/documentation/files/${fileId}`, {
            responseType: 'text'
          });
          setFileContent(response.data);
        } catch (error) {
          console.error('Error fetching file content:', error);
          setError(true);
        }
      } else {
        // For other files, get blob URL
        const url = await getAuthenticatedFileUrl(fileId);
        if (url) {
          setFileUrl(url);
        } else {
          setError(true);
        }
      }
      
      setLoading(false);
    };

    loadFile();

    // Cleanup blob URL on unmount
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileId, filename]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={style}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={style}>
        <div className="text-center">
          <FileText className="h-16 w-16 text-secondary-400 mx-auto mb-2" />
          <p className="text-sm text-secondary-600">Failed to load file</p>
        </div>
      </div>
    );
  }

  const fileExtension = filename.toLowerCase().split('.').pop();

  if (fileExtension === 'pdf') {
    return (
      <iframe
        src={`${fileUrl}#toolbar=0`}
        className={className}
        style={style}
        title={filename}
        {...props}
      />
    );
  } else if (['png', 'jpg', 'jpeg', 'gif'].includes(fileExtension)) {
    return (
      <img
        src={fileUrl}
        alt={filename}
        className={className}
        style={style}
        {...props}
      />
    );
  } else if (['mp4', 'avi', 'mov', 'webm'].includes(fileExtension)) {
    return (
      <video
        src={fileUrl}
        controls
        className={className}
        style={style}
        {...props}
      >
        Your browser does not support the video tag.
      </video>
    );
  } else if (['json', 'yaml', 'yml', 'xml', 'txt', 'md'].includes(fileExtension) && fileContent) {
    // Render actual file content for text-based files
    return (
      <div className={`${className} bg-white border border-gray-200 rounded-lg overflow-hidden`} style={style}>
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {fileExtension === 'json' ? 'JSON Content' :
             fileExtension === 'yaml' || fileExtension === 'yml' ? 'YAML Content' :
             fileExtension === 'xml' ? 'XML Content' :
             fileExtension === 'txt' ? 'Text Content' :
             fileExtension === 'md' ? 'Markdown Content' :
             'File Content'}
          </span>
          <span className="text-xs text-gray-500">
            {fileExtension.toUpperCase()}
          </span>
        </div>
        <div className="p-4 overflow-auto max-h-96">
          {fileExtension === 'json' ? (
            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
              {(() => {
                try {
                  return JSON.stringify(JSON.parse(fileContent), null, 2);
                } catch (error) {
                  return `Invalid JSON format:\n${fileContent}`;
                }
              })()}
            </pre>
          ) : (
            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
              {fileContent}
            </pre>
          )}
        </div>
      </div>
    );
  } else {
    // Render file type icons like in task activity attachments
    const fileExtension = filename.toLowerCase().split('.').pop();
    
    const getFileTypeInfo = (ext) => {
      switch (ext) {
        case 'pdf':
          return { bg: 'bg-red-50', color: 'text-red-500', label: 'PDF', icon: '📄' };
        case 'docx':
        case 'doc':
          return { bg: 'bg-blue-50', color: 'text-blue-500', label: 'Word', icon: '📝' };
        case 'xlsx':
        case 'xls':
          return { bg: 'bg-green-50', color: 'text-green-500', label: 'Excel', icon: '📊' };
        case 'json':
          return { bg: 'bg-purple-50', color: 'text-purple-500', label: 'JSON', icon: '📄' };
        case 'yaml':
        case 'yml':
          return { bg: 'bg-purple-50', color: 'text-purple-500', label: 'YAML', icon: '📄' };
        case 'xml':
          return { bg: 'bg-purple-50', color: 'text-purple-500', label: 'XML', icon: '📄' };
        case 'txt':
          return { bg: 'bg-purple-50', color: 'text-purple-500', label: 'Text', icon: '📄' };
        case 'md':
          return { bg: 'bg-purple-50', color: 'text-purple-500', label: 'Markdown', icon: '📄' };
        default:
          return { bg: 'bg-gray-50', color: 'text-gray-500', label: 'File', icon: '📎' };
      }
    };

    const fileInfo = getFileTypeInfo(fileExtension);
    
    return (
      <div className={`${className} ${fileInfo.bg} rounded-lg flex items-center justify-center`} style={style}>
        <div className="text-center">
          <div className={`${fileInfo.color} text-4xl mb-2`}>{fileInfo.icon}</div>
          <div className={`${fileInfo.color.replace('500', '700')} text-xs font-medium`}>{fileInfo.label}</div>
        </div>
      </div>
    );
  }
};

const RichTextEditor = ({ value, onChange, placeholder = "Start writing...", attachments = [], onAttachmentAdd, onAttachmentRemove }) => {
  const editorRef = useRef(null);
  const [isToolbarVisible, setIsToolbarVisible] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [linkForm, setLinkForm] = useState({ text: '', url: '', type: 'external' });
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    
    // Get pasted content
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');
    
    if (pastedData) {
      // Sanitize the content to remove local file references
      const sanitizedContent = sanitizePastedContent(pastedData);
      
      // Insert sanitized content
      document.execCommand('insertHTML', false, sanitizedContent);
      handleInput();
    }
  };

  const sanitizePastedContent = (html) => {
    // Create a temporary div to parse and clean the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Remove all images with local file:// URLs
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => {
      const src = img.getAttribute('src');
      if (src && (src.startsWith('file://') || src.includes('msohtmlclip') || src.includes('AppData'))) {
        // Replace with placeholder text or remove entirely
        const placeholder = document.createElement('span');
        placeholder.textContent = '[Image removed - please use the Upload button to add images]';
        placeholder.className = 'text-gray-500 italic bg-gray-100 px-2 py-1 rounded';
        img.parentNode.replaceChild(placeholder, img);
      }
    });
    
    // Remove any other elements with local file references
    const allElements = tempDiv.querySelectorAll('*');
    allElements.forEach(element => {
      // Check all attributes for local file references
      Array.from(element.attributes).forEach(attr => {
        if (attr.value && (attr.value.startsWith('file://') || attr.value.includes('msohtmlclip') || attr.value.includes('AppData'))) {
          element.removeAttribute(attr.name);
        }
      });
    });
    
    return tempDiv.innerHTML;
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertLink = () => {
    setShowLinkModal(true);
  };

  const handleInsertLink = () => {
    if (linkForm.url && linkForm.text) {
      // Create a more robust link with proper styling and icon based on type
      let linkIcon = '';
      let linkClass = 'text-blue-600 underline hover:text-blue-800 transition-colors';
      
      switch (linkForm.type) {
        case 'api':
          linkIcon = '🔗 ';
          linkClass = 'text-purple-600 underline hover:text-purple-800 transition-colors';
          break;
        case 'swagger':
          linkIcon = '📋 ';
          linkClass = 'text-green-600 underline hover:text-green-800 transition-colors';
          break;
        case 'postman':
          linkIcon = '📮 ';
          linkClass = 'text-orange-600 underline hover:text-orange-800 transition-colors';
          break;
        case 'github':
          linkIcon = '🔧 ';
          linkClass = 'text-gray-800 underline hover:text-gray-900 transition-colors';
          break;
        case 'live':
          linkIcon = '🌐 ';
          linkClass = 'text-indigo-600 underline hover:text-indigo-800 transition-colors';
          break;
        default:
          linkIcon = '🔗 ';
      }
      
      const linkHtml = `<a href="${linkForm.url}" target="_blank" rel="noopener noreferrer" class="${linkClass}" style="display: inline-flex; align-items: center; gap: 4px; margin: 2px 0;">${linkIcon}${linkForm.text}</a>`;
      
      // Focus the editor first
      if (editorRef.current) {
        editorRef.current.focus();
      }
      
      document.execCommand('insertHTML', false, linkHtml);
      setShowLinkModal(false);
      setLinkForm({ text: '', url: '', type: 'external' });
      
      // Ensure the change is registered
      setTimeout(() => handleInput(), 100);
    } else {
      toast.error('Please enter both link text and URL');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload file (you'll need to implement this endpoint)
      const response = await axios.post('/api/documentation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const attachment = {
        id: response.data.id,
        name: file.name,
        url: response.data.url,
        type: file.type,
        size: file.size
      };
      
      onAttachmentAdd && onAttachmentAdd(attachment);
      
      // Insert file link in editor
      const fileIcon = file.type.startsWith('image/') ? '🖼️' : '📄';
      const freshUrl = await getAttachmentUrl(attachment.filename || attachment.id) || attachment.url;
      const fileHtml = `<div class="attachment-link bg-gray-100 border rounded p-2 my-2 inline-block">
        <span class="mr-2">${fileIcon}</span>
        <a href="${freshUrl}" target="_blank" class="text-blue-600 underline">${attachment.name}</a>
        <span class="text-gray-500 text-sm ml-2">(${(attachment.size / 1024).toFixed(1)} KB)</span>
      </div>`;
      
      document.execCommand('insertHTML', false, fileHtml);
      handleInput();
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      setShowAttachmentModal(false);
    }
  };

  const insertCodeBlock = () => {
    const code = prompt('Enter code or API endpoint:');
    if (code) {
      const codeHtml = `<pre class="bg-gray-100 border rounded p-3 my-2 overflow-x-auto"><code>${code}</code></pre>`;
      document.execCommand('insertHTML', false, codeHtml);
      handleInput();
    }
  };

  const insertAPICollection = () => {
    setLinkForm({ text: '', url: '', type: 'api' });
    setShowLinkModal(true);
  };

  const changeFontSize = (size) => {
    execCommand('fontSize', size);
  };

  const changeFontFamily = (font) => {
    execCommand('fontName', font);
  };

  const changeTextColor = (color) => {
    execCommand('foreColor', color);
  };

  const changeBackgroundColor = (color) => {
    execCommand('backColor', color);
  };

  const changeHeading = (heading) => {
    if (heading) {
      execCommand('formatBlock', heading);
    } else {
      execCommand('formatBlock', 'div');
    }
  };

  return (
    <div className="border border-secondary-300 rounded-lg overflow-hidden">
      {/* Enhanced Toolbar */}
      {isToolbarVisible && (
        <div className="bg-gradient-to-r from-secondary-50 to-secondary-100 border-b border-secondary-300 p-2 sm:p-3 overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            {/* Text Formatting Group */}
            <div className="flex items-center bg-white rounded-lg border border-secondary-200 shadow-sm p-1">
              <button
                type="button"
                onClick={() => execCommand('bold')}
                className="p-2 hover:bg-secondary-100 rounded transition-colors group"
                title="Bold (Ctrl+B)"
              >
                <Bold className="h-4 w-4 text-secondary-600 group-hover:text-secondary-900" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('italic')}
                className="p-2 hover:bg-secondary-100 rounded transition-colors group"
                title="Italic (Ctrl+I)"
              >
                <Italic className="h-4 w-4 text-secondary-600 group-hover:text-secondary-900" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('underline')}
                className="p-2 hover:bg-secondary-100 rounded transition-colors group"
                title="Underline (Ctrl+U)"
              >
                <Underline className="h-4 w-4 text-secondary-600 group-hover:text-secondary-900" />
              </button>
            </div>

            {/* Heading Styles */}
            <div className="flex items-center bg-white rounded-lg border border-secondary-200 shadow-sm">
              <select
                onChange={(e) => changeHeading(e.target.value)}
                className="text-sm bg-transparent border-none outline-none px-3 py-2 text-secondary-700 font-medium rounded-lg"
                title="Heading Style"
              >
                <option value="">Normal Text</option>
                <option value="h1">📝 Heading 1</option>
                <option value="h2">📝 Heading 2</option>
                <option value="h3">📝 Heading 3</option>
                <option value="h4">📝 Heading 4</option>
                <option value="h5">📝 Heading 5</option>
                <option value="h6">📝 Heading 6</option>
              </select>
            </div>

            {/* Font Controls */}
            <div className="flex items-center gap-2">
              <div className="bg-white rounded-lg border border-secondary-200 shadow-sm">
                <select
                  onChange={(e) => changeFontSize(e.target.value)}
                  className="text-sm bg-transparent border-none outline-none px-3 py-2 text-secondary-700 rounded-lg"
                  title="Font Size"
                >
                  <option value="">📏 Size</option>
                  <option value="1">📏 8pt</option>
                  <option value="2">📏 10pt</option>
                  <option value="3">📏 12pt</option>
                  <option value="4">📏 14pt</option>
                  <option value="5">📏 18pt</option>
                  <option value="6">📏 24pt</option>
                  <option value="7">📏 36pt</option>
                </select>
              </div>
              
              <div className="bg-white rounded-lg border border-secondary-200 shadow-sm">
                <select
                  onChange={(e) => changeFontFamily(e.target.value)}
                  className="text-sm bg-transparent border-none outline-none px-3 py-2 text-secondary-700 rounded-lg"
                  title="Font Family"
                >
                  <option value="">🔤 Font</option>
                  <option value="Arial">🔤 Arial</option>
                  <option value="Helvetica">🔤 Helvetica</option>
                  <option value="Times New Roman">🔤 Times New Roman</option>
                  <option value="Courier New">💻 Courier New</option>
                  <option value="Georgia">🔤 Georgia</option>
                  <option value="Verdana">🔤 Verdana</option>
                </select>
              </div>
            </div>

            {/* Colors */}
            <div className="flex items-center bg-white rounded-lg border border-secondary-200 shadow-sm p-2 gap-2">
              <span className="text-xs text-secondary-500">🎨</span>
              <input
                type="color"
                onChange={(e) => changeTextColor(e.target.value)}
                className="w-8 h-8 border border-secondary-300 rounded cursor-pointer hover:border-secondary-400 transition-colors"
                title="Text Color"
              />
              <input
                type="color"
                onChange={(e) => changeBackgroundColor(e.target.value)}
                className="w-8 h-8 border border-secondary-300 rounded cursor-pointer hover:border-secondary-400 transition-colors"
                title="Background Color"
              />
            </div>

            {/* Alignment */}
            <div className="flex items-center gap-1 mr-3">
              <button
                type="button"
                onClick={() => execCommand('justifyLeft')}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Align Left"
              >
                <AlignLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('justifyCenter')}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Align Center"
              >
                <AlignCenter className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('justifyRight')}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Align Right"
              >
                <AlignRight className="h-4 w-4" />
              </button>
            </div>

            {/* Lists */}
            <div className="flex items-center gap-1 mr-3">
              <button
                type="button"
                onClick={() => execCommand('insertUnorderedList')}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Bullet List"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => execCommand('insertOrderedList')}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Numbered List"
              >
                <ListOrdered className="h-4 w-4" />
              </button>
            </div>

            {/* Advanced Tools */}
            <div className="flex items-center gap-1 mr-3">
              <button
                type="button"
                onClick={insertLink}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Insert Link"
              >
                <Link className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setShowAttachmentModal(true)}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Upload File"
              >
                <Upload className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={insertCodeBlock}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Insert Code Block"
              >
                <Code className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={insertAPICollection}
                className="p-2 hover:bg-secondary-200 rounded transition-colors"
                title="Add API Collection"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>

            {/* Headings */}
            <div className="flex items-center gap-1">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    execCommand('formatBlock', e.target.value);
                  }
                }}
                className="text-sm border border-secondary-300 rounded px-2 py-1"
                title="Heading"
              >
                <option value="">Normal</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="h4">Heading 4</option>
                <option value="h5">Heading 5</option>
                <option value="h6">Heading 6</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="min-h-[200px] sm:min-h-[300px] p-3 sm:p-4 focus:outline-none rich-text-editor"
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />

      {/* Toggle Toolbar */}
      <div className="border-t border-secondary-300 bg-secondary-50 px-3 py-2">
        <button
          type="button"
          onClick={() => setIsToolbarVisible(!isToolbarVisible)}
          className="text-sm text-secondary-600 hover:text-secondary-800 min-h-[44px] px-2"
        >
          {isToolbarVisible ? 'Hide Toolbar' : 'Show Toolbar'}
        </button>
      </div>

      {/* Enhanced Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-xl w-full max-w-lg mx-4 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <div>
                <h3 className="text-xl font-bold text-secondary-900">
                  🔗 Insert Link
                </h3>
                <p className="text-sm text-secondary-600 mt-1">
                  Add a link to your documentation
                </p>
              </div>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Link Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-3">
                  📂 Link Type
                </label>
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                  {[
                    { value: 'external', icon: '🔗', label: 'External Link', color: 'blue' },
                    { value: 'api', icon: '🔗', label: 'API Docs', color: 'purple' },
                    { value: 'swagger', icon: '📋', label: 'Swagger/OpenAPI', color: 'green' },
                    { value: 'postman', icon: '📮', label: 'Postman Collection', color: 'orange' },
                    { value: 'github', icon: '🔧', label: 'GitHub Repo', color: 'gray' },
                    { value: 'live', icon: '🌐', label: 'Live Demo', color: 'indigo' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setLinkForm(prev => ({ ...prev, type: type.value }))}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        linkForm.type === type.value
                          ? `border-${type.color}-500 bg-${type.color}-50 text-${type.color}-700`
                          : 'border-secondary-200 hover:border-secondary-300 text-secondary-600'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{type.icon}</span>
                        <span className="font-medium text-sm">{type.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Display Text
                </label>
                <input
                  type="text"
                  value={linkForm.text}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, text: e.target.value }))}
                  className="input-field"
                  placeholder={
                    linkForm.type === 'api' ? 'API Documentation' :
                    linkForm.type === 'swagger' ? 'Swagger API Docs' :
                    linkForm.type === 'postman' ? 'Postman Collection' :
                    linkForm.type === 'github' ? 'GitHub Repository' :
                    linkForm.type === 'live' ? 'Live Demo' :
                    'Link text'
                  }
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={linkForm.url}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, url: e.target.value }))}
                  className="input-field"
                  placeholder={
                    linkForm.type === 'swagger' ? 'https://api.example.com/swagger' :
                    linkForm.type === 'postman' ? 'https://documenter.getpostman.com/...' :
                    linkForm.type === 'github' ? 'https://github.com/user/repo' :
                    'https://example.com'
                  }
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-secondary-200 bg-secondary-50 rounded-b-xl">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-secondary-600 hover:text-secondary-800 hover:bg-secondary-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleInsertLink}
                disabled={!linkForm.text.trim() || !linkForm.url.trim()}
                className="px-6 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-sm"
              >
                🔗 Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {showAttachmentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-lg w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Upload File</h3>
              <button
                onClick={() => setShowAttachmentModal(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
                <Upload className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                <p className="text-secondary-600 mb-2">
                  Choose files to upload
                </p>
                <p className="text-xs text-secondary-500 mb-4">
                  Supports: Images, PDFs, Documents, Postman Collections, etc.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.json,.yaml,.yml,.txt,.md"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="btn-primary"
                >
                  {uploadingFile ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </div>
                  ) : (
                    'Choose File'
                  )}
                </button>
              </div>
              
              <div className="text-xs text-secondary-500">
                <p><strong>Supported file types:</strong></p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Images: PNG, JPG, GIF</li>
                  <li>Documents: PDF, DOC, DOCX, TXT, MD</li>
                  <li>API Collections: JSON, YAML, YML</li>
                  <li>Max file size: 10MB</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to extract plain text from HTML content
const getTextContent = (html) => {
  if (!html) return '';
  // Create a temporary div to parse HTML and extract text
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

// Helper function to create clean preview text
const getCleanPreview = (content, maxLength = 200) => {
  if (!content) return '';
  
  // Remove VML and Microsoft Office metadata
  let cleanContent = content
    .replace(/<\?xml[^>]*>/gi, '')
    .replace(/<!--\[if[^>]*>[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<v:[^>]*>[\s\S]*?<\/v:[^>]*>/gi, '')
    .replace(/<o:[^>]*>[\s\S]*?<\/o:[^>]*>/gi, '')
    .replace(/<w:[^>]*>[\s\S]*?<\/w:[^>]*>/gi, '')
    .replace(/<m:[^>]*>[\s\S]*?<\/m:[^>]*>/gi, '')
    .replace(/behavior:url\([^)]*\)/gi, '')
    .replace(/mso-[^:]*:[^;]*;?/gi, '')
    .replace(/style="[^"]*"/gi, '');
  
  // Extract plain text
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = cleanContent;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  
  // Truncate and clean up
  return plainText.trim().substring(0, maxLength) + (plainText.length > maxLength ? '...' : '');
};

// Document Card Component
const DocumentCard = ({ doc, viewMode, onView, onEdit, onDelete, onToggleFavorite, isFavorite, user, showDropdown, onToggleDropdown, onCloseDropdown }) => {
  const getDocumentIcon = (doc) => {
    // Check if it's an uploaded file and get file type from title
    if (doc.tags && doc.tags.includes('uploaded-file')) {
      const filename = doc.title.toLowerCase();
      if (filename.endsWith('.pdf')) {
        return <FileText className="h-7 w-7 text-white" />;
      } else if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
        return <FileSpreadsheet className="h-7 w-7 text-white" />;
      } else if (filename.endsWith('.docx') || filename.endsWith('.doc')) {
        return <FileType className="h-7 w-7 text-white" />;
      } else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) {
        return <FileType className="h-7 w-7 text-white" />;
      } else if (filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.gif')) {
        return <Image className="h-7 w-7 text-white" />;
      } else if (filename.endsWith('.mp4') || filename.endsWith('.avi') || filename.endsWith('.mov') || filename.endsWith('.webm')) {
        return <Video className="h-7 w-7 text-white" />;
      } else if (filename.endsWith('.json') || filename.endsWith('.yaml') || filename.endsWith('.yml')) {
        return <FileCode className="h-7 w-7 text-white" />;
      } else {
        return <Upload className="h-7 w-7 text-white" />;
      }
    }
    // Check for specific content types
    if (doc.content && doc.content.includes('📎')) {
      return <Paperclip className="h-7 w-7 text-white" />;
    }
    return <FileText className="h-7 w-7 text-white" />;
  };

  const getDocumentType = (doc) => {
    if (doc.tags && doc.tags.includes('uploaded-file')) {
      const filename = doc.title.toLowerCase();
      if (filename.endsWith('.pdf')) {
        return 'PDF Document';
      } else if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
        return 'Excel Spreadsheet';
      } else if (filename.endsWith('.docx') || filename.endsWith('.doc')) {
        return 'Word Document';
      } else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) {
        return 'PowerPoint';
      } else if (filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.gif')) {
        return 'Image';
      } else if (filename.endsWith('.mp4') || filename.endsWith('.avi') || filename.endsWith('.mov') || filename.endsWith('.webm')) {
        return 'Video';
      } else if (filename.endsWith('.json') || filename.endsWith('.yaml') || filename.endsWith('.yml')) {
        return 'Data File';
      } else {
        return 'File';
      }
    }
    if (doc.tags && doc.tags.includes('api-docs')) {
      return 'API Documentation';
    }
    if (doc.tags && doc.tags.includes('guide')) {
      return 'Guide';
    }
    return 'Document';
  };

  if (viewMode === 'list') {
    return (
      <div 
        className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-300 group cursor-pointer"
        onClick={() => onView(doc.id)}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              {getDocumentIcon(doc)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-700 transition-colors truncate">
                  {doc.title}
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(doc.id);
                  }}
                  className={`p-1 rounded transition-colors ${
                    isFavorite 
                      ? 'text-yellow-500 hover:text-yellow-600' 
                      : 'text-gray-400 hover:text-yellow-500'
                  }`}
                >
                  <Star className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                </button>
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {getDocumentType(doc)}
                </span>
                {doc.is_public ? (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                    <Globe className="h-3 w-3" />
                    <span>Public</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    <Lock className="h-3 w-3" />
                    <span>Private</span>
                  </div>
                )}
              </div>
              
              {doc.content && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2 leading-relaxed">
                  {getCleanPreview(doc.content, 150)}
                </p>
              )}
              
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>{doc.created_by_name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
                {doc.updated_at && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Updated {new Date(doc.updated_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              
              {doc.tags && doc.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {doc.tags.filter(tag => tag !== 'uploaded-file').slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                  {doc.tags.filter(tag => tag !== 'uploaded-file').length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      +{doc.tags.filter(tag => tag !== 'uploaded-file').length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {(user?.role === 'admin' || (user?.role === 'project_lead' && doc.created_by === user?.id)) && (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onEdit(doc.id)}
                  className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 rounded-lg transition-colors"
                  title="Edit Document"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete(doc)}
                  className="p-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-lg transition-colors"
                  title="Delete Document"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div 
        className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-300 group h-full flex flex-col cursor-pointer"
        onClick={() => onView(doc.id)}
      >
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              {getDocumentIcon(doc)}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(doc.id);
                }}
                className={`p-1 rounded transition-colors ${
                  isFavorite 
                    ? 'text-yellow-500 hover:text-yellow-600' 
                    : 'text-gray-400 hover:text-yellow-500'
                }`}
              >
                <Star className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <div className="relative">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDropdown(doc.id);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
                {showDropdown === doc.id && (
                  <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[120px]">
                    <button
                      onClick={() => {
                        onView(doc.id);
                        onCloseDropdown();
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                    {(user?.role === 'admin' || (user?.role === 'project_lead' && doc.created_by === user?.id)) && (
                      <>
                        <button
                          onClick={() => {
                            onEdit(doc.id);
                            onCloseDropdown();
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            onDelete(doc);
                            onCloseDropdown();
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900 group-hover:text-primary-700 transition-colors mb-2 line-clamp-2">
              {doc.title}
            </h3>
            
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {getDocumentType(doc)}
              </span>
              {doc.is_public ? (
                <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                  <Globe className="h-3 w-3" />
                  <span>Public</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                  <Lock className="h-3 w-3" />
                  <span>Private</span>
                </div>
              )}
            </div>

            {doc.content && (
              <p className="text-gray-600 text-xs mb-3 line-clamp-2 leading-relaxed">
                {getCleanPreview(doc.content, 100)}
              </p>
            )}

            {doc.tags && doc.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {doc.tags.filter(tag => tag !== 'uploaded-file').slice(0, 1).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full font-medium"
                  >
                    #{tag}
                  </span>
                ))}
                {doc.tags.filter(tag => tag !== 'uploaded-file').length > 1 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{doc.tags.filter(tag => tag !== 'uploaded-file').length - 1}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="mt-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span className="truncate">{doc.created_by_name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              {(user?.role === 'admin' || (user?.role === 'project_lead' && doc.created_by === user?.id)) && (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onEdit(doc.id)}
                    className="p-1 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 rounded transition-colors"
                    title="Edit Document"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => onDelete(doc)}
                    className="p-1 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded transition-colors"
                    title="Delete Document"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cards view (larger cards)
  return (
    <div 
      className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-300 group h-full flex flex-col cursor-pointer"
      onClick={() => onView(doc.id)}
    >
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            {getDocumentIcon(doc)}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(doc.id);
              }}
              className={`p-1 rounded transition-colors ${
                isFavorite 
                  ? 'text-yellow-500 hover:text-yellow-600' 
                  : 'text-gray-400 hover:text-yellow-500'
              }`}
            >
              <Star className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleDropdown(doc.id);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {showDropdown === doc.id && (
                <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[120px]">
                  <button
                    onClick={() => {
                      onView(doc.id);
                      onCloseDropdown();
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </button>
                  {(user?.role === 'admin' || (user?.role === 'project_lead' && doc.created_by === user?.id)) && (
                    <>
                      <button
                        onClick={() => {
                          onEdit(doc.id);
                          onCloseDropdown();
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          onDelete(doc);
                          onCloseDropdown();
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900 group-hover:text-primary-700 transition-colors mb-2 line-clamp-2">
            {doc.title}
          </h3>
        
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {getDocumentType(doc)}
            </span>
            {doc.is_public ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                <Globe className="h-3 w-3" />
                <span>Public</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                <Lock className="h-3 w-3" />
                <span>Private</span>
              </div>
            )}
          </div>

          {doc.content && (
            <p className="text-gray-600 text-xs mb-3 line-clamp-3 leading-relaxed">
              {getCleanPreview(doc.content, 150)}
            </p>
          )}

          {doc.tags && doc.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {doc.tags.filter(tag => tag !== 'uploaded-file').slice(0, 2).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full font-medium"
                >
                  #{tag}
                </span>
              ))}
              {doc.tags.filter(tag => tag !== 'uploaded-file').length > 2 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{doc.tags.filter(tag => tag !== 'uploaded-file').length - 2} more
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{doc.created_by_name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(doc.created_at).toLocaleDateString()}</span>
            </div>
            {doc.updated_at && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Updated {new Date(doc.updated_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto">
          <div className="flex items-center justify-end">
            {(user?.role === 'admin' || (user?.role === 'project_lead' && doc.created_by === user?.id)) && (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onEdit(doc.id)}
                  className="p-1 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 rounded transition-colors"
                  title="Edit Document"
                >
                  <Edit className="h-3 w-3" />
                </button>
                <button
                  onClick={() => onDelete(doc)}
                  className="p-1 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded transition-colors"
                  title="Delete Document"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Documentation = ({ projectId, onBack }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: [],
    is_public: true,
    attachments: []
  });

  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'list', 'grid', 'cards'
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'updated_at', 'title', 'author'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [showFilters, setShowFilters] = useState(false);
  const [favoriteDocs, setFavoriteDocs] = useState(new Set());
  const [showDropdown, setShowDropdown] = useState(null); // Track which dropdown is open
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);

  useEffect(() => {
    fetchDocuments();
  }, [projectId, currentPage]);

  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Add keyboard shortcuts for save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showEditor && (e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!saving && formData.title.trim() && formData.content.trim()) {
          handleSave();
        }
      }
      if (showEditor && e.key === 'Escape') {
        setShowEditor(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showEditor, saving, formData.title, formData.content]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        project_id: projectId,
        page: currentPage,
        page_size: '10'
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Remove selectedTags from API call - we'll handle tag filtering client-side

      const response = await axios.get(`/api/documentation?${params.toString()}`);
      setDocuments(response.data.results || []);
      setTotalDocuments(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / 10));
      
      // Extract unique tags
      const allTags = new Set();
      response.data.results.forEach(doc => {
        doc.tags.forEach(tag => allTags.add(tag));
      });
      setAvailableTags(Array.from(allTags));
    } catch (error) {
      toast.error('Failed to fetch documentation');
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchDocuments();
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]); // Remove selectedTags from dependencies

  // Client-side filtering and sorting for documents
  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents;
    
    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(doc => {
      return selectedTags.some(selectedTag => 
        doc.tags.some(docTag => docTag === selectedTag)
      );
    });
    }
    
    // Sort documents
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'author':
          aValue = a.created_by_name.toLowerCase();
          bValue = b.created_by_name.toLowerCase();
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || a.created_at);
          bValue = new Date(b.updated_at || b.created_at);
          break;
        default: // created_at
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [documents, selectedTags, sortBy, sortOrder]);

  const toggleFavorite = (docId) => {
    setFavoriteDocs(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(docId)) {
        newFavorites.delete(docId);
      } else {
        newFavorites.add(docId);
      }
      return newFavorites;
    });
  };

  const toggleDropdown = (docId) => {
    setShowDropdown(showDropdown === docId ? null : docId);
  };

  const closeDropdown = () => {
    setShowDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleCreateNew = () => {
    setEditingDoc(null);
    setFormData({
      title: '',
      content: '',
      tags: [],
      is_public: true,
      attachments: []
    });
    setShowEditor(true);
  };

  const handleAttachmentAdd = (attachment) => {
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, attachment]
    }));
  };

  const handleAttachmentRemove = (attachmentId) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== attachmentId)
    }));
  };

  const handleView = async (docId) => {
    try {
      const response = await axios.get(`/api/documentation/${docId}`);
      const doc = response.data;
      
      setViewingDoc(doc);
      setShowViewer(true);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleEdit = async (docId) => {
    try {
      const response = await axios.get(`/api/documentation/${docId}`);
      const doc = response.data;
      
      setEditingDoc(doc);
      setFormData({
        title: doc.title,
        content: doc.content,
        tags: doc.tags || [],
        is_public: doc.is_public,
        attachments: doc.attachments || []
      });
      setShowEditor(true);
      setShowViewer(false);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleDownload = async (docId) => {
    try {
      const response = await axios.get(`/api/documentation/${docId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${viewingDoc?.title || 'document'}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (!getTextContent(formData.content).trim()) {
      toast.error('Please enter some content');
      return;
    }

    setSaving(true);
    
    try {
      const docData = {
        ...formData,
        project_id: projectId
      };

      if (editingDoc) {
        await axios.put(`/api/documentation/${editingDoc.id}`, docData);
        toast.success('Document updated successfully');
      } else {
        await axios.post('/api/documentation', docData);
        toast.success('Document created successfully');
      }

      setShowEditor(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (doc) => {
    setDeletingDoc(doc);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDoc) return;

    setIsDeleting(true);
    try {
      await axios.delete(`/api/documentation/${deletingDoc.id}`);
      toast.success('Document deleted successfully');
      fetchDocuments();
      setShowDeleteModal(false);
      setDeletingDoc(null);
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingDoc(null);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const toggleTagFilter = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // File upload functions
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (fileType.startsWith('video/')) return <Video className="h-4 w-4" />;
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return <FileSpreadsheet className="h-4 w-4" />;
    if (fileType.includes('pdf')) return <FileText className="h-4 w-4" />;
    if (fileType.includes('word') || fileType.includes('document')) return <FileType className="h-4 w-4" />;
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return <FileType className="h-4 w-4" />;
    if (fileType.includes('json') || fileType.includes('postman') || fileType.includes('swagger')) return <FileCode className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/json',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/avi',
      'video/mov',
      'video/wmv',
      'video/webm'
    ];

    if (file.size > maxSize) {
      return `File size must be less than 10MB. Current size: ${formatFileSize(file.size)}`;
    }

    if (!allowedTypes.includes(file.type)) {
      return `File type not supported. Allowed types: PDF, Word, PowerPoint, Excel, Images, Videos, JSON (Postman/Swagger)`;
    }

    return null;
  };

  const handleFileUpload = async (files) => {
    setIsUploading(true);
    const uploadPromises = [];

    for (const file of files) {
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(`${file.name}: ${validationError}`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      const uploadPromise = axios.post('/api/documentation/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          project_id: projectId,
          title: file.name,
          is_public: true
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: progress
          }));
        }
      }).then(response => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        return { file, success: true, response: response.data };
      }).catch(error => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        return { file, success: false, error: error.response?.data?.detail || 'Upload failed' };
      });

      uploadPromises.push(uploadPromise);
    }

    try {
      const results = await Promise.all(uploadPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        toast.success(`Successfully uploaded ${successful.length} file(s)`);
        fetchDocuments(); // Refresh the document list
      }

      if (failed.length > 0) {
        failed.forEach(result => {
          toast.error(`${result.file.name}: ${result.error}`);
        });
      }

      setShowUploadModal(false);
      setUploadedFiles([]);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    
    // Limit to 5 files
    if (files.length > 5) {
      toast.error('Maximum 5 files allowed. Only the first 5 files will be selected.');
      setUploadedFiles(files.slice(0, 5));
    } else {
      setUploadedFiles(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 5 files
    if (files.length > 5) {
      toast.error('Maximum 5 files allowed. Only the first 5 files will be selected.');
      setUploadedFiles(files.slice(0, 5));
    } else {
      setUploadedFiles(files);
    }
  };

  if (showEditor) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowEditor(false)}
              className="p-2 text-secondary-400 hover:text-secondary-600 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-secondary-900">
              {editingDoc ? 'Edit Document' : 'New Document'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowEditor(false)}
              className="btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.title.trim() || !getTextContent(formData.content).trim()}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>

        {/* Editor Form */}
        <div className="card space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="input-field text-lg font-medium"
              placeholder="Enter document title..."
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Tags
            </label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className="input-field flex-1"
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="btn-secondary"
              >
                <Tag className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full flex items-center gap-1"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-primary-600 hover:text-primary-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                className="rounded border-secondary-300"
              />
              <span className="text-sm font-medium text-secondary-700 flex items-center gap-1">
                {formData.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                Public document (visible to all project members)
              </span>
            </label>
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Content *
            </label>
            <RichTextEditor
              value={formData.content}
              onChange={(content) => setFormData(prev => ({ ...prev, content }))}
              placeholder="Start writing your documentation..."
              attachments={formData.attachments}
              onAttachmentAdd={handleAttachmentAdd}
              onAttachmentRemove={handleAttachmentRemove}
            />
          </div>

          {/* Attachments Section */}
          {formData.attachments && formData.attachments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Attachments ({formData.attachments.length})
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {formData.attachments.map((attachment) => (
                  <div key={attachment.id} className="border border-secondary-200 rounded-lg p-3 flex items-center gap-3">
                    <div className="flex-shrink-0">
                      {attachment.type?.startsWith('image/') ? (
                        <Image className="h-8 w-8 text-green-600" />
                      ) : attachment.name?.endsWith('.json') || attachment.name?.endsWith('.yaml') ? (
                        <Code className="h-8 w-8 text-blue-600" />
                      ) : (
                        <File className="h-8 w-8 text-secondary-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-secondary-900 truncate">
                        {attachment.name}
                      </h4>
                      <p className="text-xs text-secondary-500">
                        {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={async () => {
                          const freshUrl = await getAttachmentUrl(attachment.filename || attachment.id);
                          window.open(freshUrl || attachment.url, '_blank');
                        }}
                        className="p-1 text-secondary-400 hover:text-blue-600 transition-colors"
                        title="Open file"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleAttachmentRemove(attachment.id)}
                        className="p-1 text-secondary-400 hover:text-red-600 transition-colors"
                        title="Remove attachment"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>


      </div>
    );
  }

  // Document Viewer Mode
  if (showViewer && viewingDoc) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="card mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={() => {
                  setShowViewer(false);
                  setViewingDoc(null);
                }}
                className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-secondary-50 rounded-lg transition-all duration-200 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-semibold text-secondary-900 truncate">{viewingDoc.title}</h1>
                <div className="flex items-center gap-2 text-xs text-secondary-600">
                  <span>By {viewingDoc.created_by_name}</span>
                  <span>•</span>
                  <span>{new Date(viewingDoc.created_at).toLocaleDateString()}</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    {viewingDoc.is_public ? (
                      <><Globe className="h-3 w-3" /> Public</>
                    ) : (
                      <><Lock className="h-3 w-3" /> Private</>
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={async () => {
                  if (viewingDoc.tags?.includes('uploaded-file') && viewingDoc.attachments && viewingDoc.attachments.length > 0) {
                    const attachment = viewingDoc.attachments[0];
                    await openDocumentWithFreshUrl(viewingDoc.id, attachment.name);
                  } else {
                    handleDownload(viewingDoc.id);
                  }
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
              {(user?.role === 'admin' || user?.role === 'project_lead') && !viewingDoc.tags?.includes('uploaded-file') && (
                <button
                  onClick={() => handleEdit(viewingDoc.id)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Document Content */}
        <main className="flex-1">
          {viewingDoc.tags?.includes('uploaded-file') ? (
            <>
              {/* Tags */}
              {viewingDoc.tags && viewingDoc.tags.length > 0 && (
                <div className="px-4 pt-4 pb-3 w-full">
                  <div className="flex flex-wrap gap-2">
                    {viewingDoc.tags.filter(tag => tag !== 'uploaded-file').map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Content */}
              {viewingDoc.attachments && viewingDoc.attachments.length > 0 && 
                viewingDoc.attachments.map((attachment) => {
                  const filename = attachment.name || viewingDoc.title;
                  const fileExtension = filename.toLowerCase().split('.').pop();
                  
                  return (
                    <div key={attachment.id} className="h-[calc(100vh-200px)] w-full">
                      {fileExtension === 'pdf' ? (
                        <div className="w-full h-full flex flex-col">
                          {/* PDF Controls */}
                          <div className="flex items-center justify-between p-3 bg-secondary-50 border-b border-secondary-200">
                            <div className="flex items-center gap-2">
                              <FileText className="h-5 w-5 text-red-600" />
                              <span className="text-sm font-medium text-secondary-700">{filename}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                                className="px-3 py-1.5 bg-secondary-600 text-white text-sm font-medium rounded-md hover:bg-secondary-700 transition-colors flex items-center gap-1.5"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Open
                              </button>
                            </div>
                          </div>
                          
                          {/* PDF Preview */}
                          <div className="flex-1">
                            <AuthenticatedFilePreview
                              fileId={viewingDoc.id}
                              filename={filename}
                              className="w-full h-full"
                            />
                          </div>
                        </div>
                      ) : fileExtension === 'png' || fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'gif' ? (
                        <div className="flex justify-center bg-gray-50 p-4 h-full">
                          <AuthenticatedFilePreview
                            fileId={viewingDoc.id}
                            filename={filename}
                            className="max-w-full h-auto max-h-full rounded-lg"
                          />
                        </div>
                      ) : fileExtension === 'mp4' || fileExtension === 'avi' || fileExtension === 'mov' || fileExtension === 'webm' ? (
                        <AuthenticatedFilePreview
                          fileId={viewingDoc.id}
                          filename={filename}
                          className="w-full h-full"
                        />
                      ) : fileExtension === 'json' || fileExtension === 'yaml' || fileExtension === 'yml' || fileExtension === 'txt' || fileExtension === 'md' ? (
                        <div className="h-full flex flex-col">
                          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                            <h4 className="text-sm font-medium text-gray-700">File Content</h4>
                            <button
                              onClick={async () => {
                                const url = await getAuthenticatedFileUrl(viewingDoc.id);
                                if (url) {
                                  window.open(url, '_blank');
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Open in new tab
                            </button>
                          </div>
                          <AuthenticatedFilePreview
                            fileId={viewingDoc.id}
                            filename={filename}
                            className="w-full flex-1"
                          />
                        </div>
                      ) : fileExtension === 'docx' || fileExtension === 'doc' || fileExtension === 'xlsx' || fileExtension === 'xls' ? (
                        <div className="w-full h-full flex flex-col">
                          {/* File Controls */}
                          <div className="flex items-center justify-between p-3 bg-secondary-50 border-b border-secondary-200">
                            <div className="flex items-center gap-2">
                              {fileExtension === 'xlsx' || fileExtension === 'xls' ? (
                                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                              ) : (
                                <FileType className="h-5 w-5 text-blue-600" />
                              )}
                              <span className="text-sm font-medium text-secondary-700">{filename}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                                className="px-3 py-1.5 bg-secondary-600 text-white text-sm font-medium rounded-md hover:bg-secondary-700 transition-colors flex items-center gap-1.5"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Open
                              </button>
                            </div>
                          </div>
                          
                          {/* Preview */}
                          <div className="flex-1">
                            <OfficePreview fileId={viewingDoc.id} filename={filename} />
                          </div>
                          <div className="hidden w-full h-full flex-col items-center justify-center bg-gray-50">
                            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                              {fileExtension === 'xlsx' || fileExtension === 'xls' ? (
                                <FileSpreadsheet className="h-10 w-10 text-gray-500" />
                              ) : (
                                <FileType className="h-10 w-10 text-gray-500" />
                              )}
                            </div>
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">
                              {fileExtension === 'xlsx' || fileExtension === 'xls' ? 'Excel Spreadsheet' : 'Word Document'}
                            </h4>
                            <p className="text-gray-600 mb-8 max-w-md text-center">
                              {fileExtension === 'xlsx' || fileExtension === 'xls' 
                                ? 'Preview not available. Click below to open or download the spreadsheet.' 
                                : 'Preview not available. Click below to open or download the document.'}
                            </p>
                            <div className="flex gap-3">
                              <button
                                onClick={() => openDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                              >
                                Open in New Tab
                              </button>
                              <button
                                onClick={() => downloadDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-[400px] bg-gray-50 flex flex-col items-center justify-center">
                          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                            {fileExtension === 'pptx' || fileExtension === 'ppt' ? (
                              <FileType className="h-10 w-10 text-gray-500" />
                            ) : (
                              <File className="h-10 w-10 text-gray-500" />
                            )}
                          </div>
                          <h4 className="text-xl font-semibold text-gray-900 mb-2">
                            {fileExtension === 'pptx' || fileExtension === 'ppt' ? 'PowerPoint Presentation' : 'File Preview'}
                          </h4>
                          <p className="text-gray-600 mb-8 max-w-md text-center">
                            {fileExtension === 'pptx' || fileExtension === 'ppt' 
                              ? 'Preview not available. Click below to open or download the presentation.'
                              : 'Preview not available for this file type.'}
                          </p>
                          <div className="flex gap-3">
                            <button
                              onClick={() => openDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                              Open in New Tab
                            </button>
                            <button
                              onClick={() => downloadDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              }
            </>
          ) : (
            <div className="p-4">
              <div className="w-full">
                {/* Tags */}
                {viewingDoc.tags && viewingDoc.tags.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {viewingDoc.tags.filter(tag => tag !== 'uploaded-file').map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="card">
                  <div className="prose max-w-none rich-text-editor min-h-[400px]"
                    dangerouslySetInnerHTML={{ __html: viewingDoc.content || '<p class="text-secondary-500 italic">No content available</p>' }}
                  />
                </div>

                {/* Attachments - Only show for non-uploaded files */}
                {viewingDoc.attachments && viewingDoc.attachments.length > 0 && (
                  <div className="mt-6 card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Paperclip className="h-5 w-5" />
                      Attachments ({viewingDoc.attachments.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {viewingDoc.attachments.map((attachment) => (
                        <div key={attachment.id} className="border border-gray-200 rounded-lg p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                          <div className="flex-shrink-0">
                            {(() => {
                              const filename = attachment.name?.toLowerCase() || '';
                              if (filename.endsWith('.pdf')) {
                                return <FileText className="h-5 w-5 text-primary-600" />;
                              } else if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
                                return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
                              } else if (filename.endsWith('.docx') || filename.endsWith('.doc')) {
                                return <FileType className="h-5 w-5 text-blue-600" />;
                              } else if (filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.gif')) {
                                return <Image className="h-5 w-5 text-purple-600" />;
                              } else {
                                return <File className="h-5 w-5 text-gray-600" />;
                              }
                            })()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{attachment.name}</p>
                            <p className="text-xs text-gray-500">
                              {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <button
                              onClick={() => openDocumentWithFreshUrl(viewingDoc.id, attachment.name)}
                              className="text-primary-600 hover:text-primary-700 transition-colors"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Documentation</h1>
            <p className="text-secondary-600">Project documentation and guides</p>
          </div>
        </div>
        {(user?.role === 'admin' || user?.role === 'project_lead') && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Upload Document
            </button>
          <button
            onClick={handleCreateNew}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New Document
          </button>
          </div>
        )}
      </div>

      {/* Search and Controls */}
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              className="input-field pl-10 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                title="List View"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                title="Grid View"
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                title="Card View"
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>

            <div className="relative">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="created_at-desc">Newest First</option>
                <option value="created_at-asc">Oldest First</option>
                <option value="updated_at-desc">Recently Updated</option>
                <option value="title-asc">Title A-Z</option>
                <option value="title-desc">Title Z-A</option>
                <option value="author-asc">Author A-Z</option>
              </select>
              <ChevronDown className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters || selectedTags.length > 0
                  ? 'bg-primary-100 text-primary-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Toggle Filters"
            >
              <Filter className="h-4 w-4" />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg p-4">
            <div className="space-y-4">
              {availableTags.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Filter by tags:</p>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTagFilter(tag)}
                        className={`px-3 py-1 text-sm rounded-full transition-colors ${
                          selectedTags.includes(tag)
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  <span>{filteredAndSortedDocuments.length} documents</span>
                </div>
                {selectedTags.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Filter className="h-4 w-4" />
                    <span>{selectedTags.length} filter{selectedTags.length > 1 ? 's' : ''} applied</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents Display */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredAndSortedDocuments.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || selectedTags.length > 0 ? 'No documents match your search' : 'No documentation yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedTags.length > 0 
              ? 'Try adjusting your search terms or filters'
              : (user?.role === 'admin' || user?.role === 'project_lead')
                ? 'Create your first document to start building project documentation'
                : 'No documentation has been created for this project yet'
            }
          </p>
          {!searchTerm && selectedTags.length === 0 && (user?.role === 'admin' || user?.role === 'project_lead') && (
            <button
              onClick={handleCreateNew}
              className="btn-primary"
            >
              Create Document
            </button>
          )}
        </div>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
            : viewMode === 'cards'
            ? 'grid grid-cols-1 lg:grid-cols-2 gap-6'
            : 'space-y-4'
        }>
          {filteredAndSortedDocuments.map((doc) => (
            <DocumentCard 
              key={doc.id} 
              doc={doc} 
              viewMode={viewMode}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onToggleFavorite={toggleFavorite}
              isFavorite={favoriteDocs.has(doc.id)}
              user={user}
              showDropdown={showDropdown}
              onToggleDropdown={toggleDropdown}
              onCloseDropdown={closeDropdown}
            />
          ))}
        </div>
        )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingDoc && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-lg w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Delete Document</h2>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this document? This action cannot be undone.
              </p>
              
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-red-900 truncate">
                      {deletingDoc.title}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-red-700 mt-1">
                      <span>By {deletingDoc.created_by_name}</span>
                      <span>•</span>
                      <span>{new Date(deletingDoc.created_at).toLocaleDateString()}</span>
                    </div>
                    {deletingDoc.tags && deletingDoc.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {deletingDoc.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {deletingDoc.tags.length > 3 && (
                          <span className="text-xs text-red-600">
                            +{deletingDoc.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="btn-secondary px-4 py-2 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Upload Documents</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFiles([]);
                  setUploadProgress({});
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer"
                onDrop={(e) => {
                  if (uploadedFiles.length >= 5) {
                    toast.error('Maximum 5 files allowed. Remove some files before adding more.');
                    return;
                  }
                  handleFileDrop(e);
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => e.preventDefault()}
                onClick={() => {
                  if (uploadedFiles.length >= 5) {
                    toast.error('Maximum 5 files allowed. Remove some files before adding more.');
                    return;
                  }
                  document.getElementById('file-input').click();
                }}
              >
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Drop files here or click to browse
                </h3>
                <p className="text-gray-500 mb-4">
                  Supported formats: PDF, Word, PowerPoint, Excel, Images, Videos, JSON (Postman/Swagger)
                </p>
                <p className="text-sm text-gray-400">
                  Maximum file size: 10MB per file • Maximum 5 files
                </p>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.wmv,.webm,.json"
                />
              </div>

              {uploadedFiles.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Selected Files ({uploadedFiles.length}/5)
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                            {getFileIcon(file.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {uploadProgress[file.name] !== undefined && (
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress[file.name]}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {uploadProgress[file.name]}%
                              </span>
                            </div>
                          )}
                          <button
                            onClick={() => {
                              setUploadedFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Button */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadedFiles([]);
                      setUploadProgress({});
                    }}
                    className="btn-secondary"
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleFileUpload(uploadedFiles)}
                    disabled={isUploading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload {uploadedFiles.length} File{uploadedFiles.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-secondary-600">
            Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalDocuments)} of {totalDocuments} documents
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-secondary-200 hover:bg-secondary-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentation;
